import { Box, Card, CardContent, IconButton, Typography } from '@mui/material'
import React, { useState } from 'react'
import MainLayout from '../../../layouts/main/main_layout'
import { useNavigate } from 'react-router-dom';
import { ArrowBack, PersonAddAlt1Rounded } from '@mui/icons-material';
import Stepper from '../components/stepper/stepper';
import PersonalDetails from '../components/personal_details/personal_details';
import LocationDetailsComp from '../components/location_details/location_details';
import OtherDetailsComp from '../components/other_details/other_details';
import { useQuery } from 'react-query';
import { getMasterData } from '../../../services/choices/choices_services';
import { SchemeMaster } from '../../schemes/models/scheme_master';
import DurableDetailsComp from '../components/durable_details/durable_details';
import { createUserService } from '../../../services/user_append_service/create_modify_user_service';
import { LocationDetails, OtherDetails, PersonelDetails } from '../model/create_user';
import { useFeedback } from '../../../providers/feedback/feeedback';
import { queryClient } from '../../../providers/query/query';
import AreaOfInterest from '../components/area_of_interest/area_of_interest';
import { black, greyEnabled, purple, purpleLight } from '../../../theme/colors';
import { DEFAULT_ORG_ID } from '../../../constants';
import useUser from '../../../providers/user/user_provider';
import { useSelectedOrgStore } from '../../../store/use-selected-org-store';
import OrgSelector from '../../../components/org_selector/org_selector';

export default function CreateUser() {
    const navigate = useNavigate();
    const { userPermissions } = useUser();
    const { selectedOrgId } = useSelectedOrgStore();
    // A super-admin/Platform-org caller picking an org below creates the
    // user there; otherwise falls back to the caller's own org, matching
    // add_new_client.tsx's pattern for portal admin accounts.
    const targetOrgId = selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;
    const [stepperCount, setStepperCount] = useState(1);
    const [uid, setUid] = useState<number | null>(null);
    const [personalDeatails, setPersonalDetails] = useState<PersonelDetails | null>(null);
    const [locationDetails, setLocationDetails] = useState<LocationDetails | null>(null);
    const [otherDetails, setOtherDetails] = useState<OtherDetails | null>(null);
    const [durableDetails, setDurableDetails] = useState<number[] | null>(null);
    const [areaOfInterest, setAreaOfInterest] = useState<number[] | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { showSnackbar } = useFeedback();

    const { data: masterDataList, isLoading: isMasterDataLoading } = useQuery<SchemeMaster>(["masterDataList", targetOrgId], async () => {
        // Pass the org picked above explicitly - the backend otherwise
        // resolves org-scoped master data (gender/maritalStatus/areasOfInterest/
        // etc.) off the caller's own session org, which has no connection to
        // the org selected in this form for a multi-org/platform admin.
        let response = await getMasterData(targetOrgId);
        return response.data.data || [];
    });

    function createUserApiCall() {
        setIsSubmitting(true);
        createUserService({
            address_line1: locationDetails?.houseNumber!,
            address_line2: locationDetails?.buildingName!,
            address_line3: locationDetails?.streetName!,
            area: locationDetails?.locality!,
            areas_of_interest_id: areaOfInterest!,
            district_id: locationDetails?.district?.id!,
            dob: personalDeatails?.dob!,
            durable_ownership_id: durableDetails,
            education_id: otherDetails?.educational?.id!,
            family_income_id: otherDetails?.familyIncome?.id!,
            family_type_id: otherDetails?.familyType?.id!,
            gender_id: personalDeatails?.gender?.id ?? null,
            house_ownership_id: otherDetails?.ownershipOfHouse?.id!,
            landmark: locationDetails?.landmark!,
            lat: '',
            lon: '',
            marital_status_id: personalDeatails?.meritalStatus?.id!,
            mobile_no: personalDeatails?.mobileNumber!,
            name: personalDeatails?.fullName!,
            org_id: targetOrgId,
            id_proof_type: personalDeatails?.idProofType || null,
            id_proof_number: personalDeatails?.idProofNumber || null,
            occupation_id: otherDetails?.occupational?.id!,
            personal_income_id: otherDetails?.personalIncome?.id!,
            pincode: locationDetails?.pincode!,
            preferred_language: 'en',
            state_id: locationDetails?.state!?.id,
            working_status_id: otherDetails?.workingStatus?.id!,
            is_subscribed: personalDeatails?.subscribeToNewsLatter?.value === 'Yes' ? true : false
        }).then((res) => {
            if (res.status === 200) {
                showSnackbar("User added successfully");
                queryClient.invalidateQueries({ queryKey: ["app-Users"] });
                navigate('/users')
            } else {
                setIsSubmitting(false);
            }
        }).catch((e) => {
            setIsSubmitting(false);
            if (+e.response?.status === 500) {
                let jsonParsedData = JSON.parse(e.response?.data?.error?.message);
                showSnackbar(jsonParsedData?.details?.[0]?.message || jsonParsedData?.message)
            } else {
                showSnackbar("Something went Wrong");
            }
        });
    }


    let stepperData: { id: number, name: string }[] = [
        {
            id: 1, name: 'Personal Details',
        },
        {
            id: 2, name: 'Location Details',
        },
        {
            id: 3, name: 'Other Details',
        },
        {
            id: 4, name: 'Durable Details',
        },
        {
            id: 5, name: 'Area of interest',
        },
    ]

    function renderCompBasedOnStepper() {
        switch (stepperCount) {
            case 1:
                return <PersonalDetails
                    onSubmit={async (data) => {
                        console.log(data);
                        setPersonalDetails(data)
                    }}
                    setUid={setUid}
                    uid={uid}
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    personalDeatails={personalDeatails!}

                />
            case 2:
                return <LocationDetailsComp
                    onSubmit={async (data) => {
                        setLocationDetails(data)
                    }}
                    setUid={setUid}
                    uid={uid}
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    locationDetails={locationDetails!}
                />
            case 3:
                return <OtherDetailsComp
                    onSubmit={async (data) => {
                        setOtherDetails(data)
                    }}
                    setUid={setUid}
                    uid={uid}
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    otherDetails={otherDetails!}
                />
            case 4:
                return <DurableDetailsComp
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    masterDataLoading={isMasterDataLoading}
                    onSubmit={() => {
                        setStepperCount(stepperCount + 1)
                    }}
                    durableDetails={durableDetails!}
                    setDurableDetails={setDurableDetails}
                />
            case 5:
                return <AreaOfInterest
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    masterDataLoading={isMasterDataLoading}
                    onSubmit={() => {
                        createUserApiCall();
                    }}
                    areaOfInterest={areaOfInterest!}
                    setAreaOfInterest={setAreaOfInterest}
                    submitting={isSubmitting}
                />
            default:
        }
    }
    return (
        <MainLayout>
            <Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: purpleLight,
                                flexShrink: 0,
                            }}
                        >
                            <PersonAddAlt1Rounded sx={{ color: purple, fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={700} color={black}>
                                Create User
                            </Typography>
                            <Typography variant="body2" color={greyEnabled}>
                                Fill in the details below across all steps to add a new user.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Card
                    elevation={0}
                    sx={{ width: '100%', mt: 2, borderRadius: 3, border: '1px solid #ECECEC' }}
                >
                    <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                        <Box mb={3} maxWidth={360}>
                            <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
                        </Box>
                        <Box>
                            <Stepper
                                stepperData={stepperData}
                                stepperCount={stepperCount}
                                setStepperCount={setStepperCount}
                            />
                            {renderCompBasedOnStepper()}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </MainLayout>
    )
}
